import {
  getAveragePrice,
  getMarketWatch,
  getNews,
  getOffers,
  getRecentStation,
  getRecommendedPrice,
} from '@/services/dashboard';

import type { TGetAveragePricePayload, TMarketWatchPeriod } from '@/types/dashboard';

import { stableParams, unwrapData } from './utils';

/**
 * Keys of the dashboard area. Every dashboard query starts with `dashboardKeys.all`.
 * The stat cards and the top categories card read the analytics header and category insights, so
 * their keys live in `analyticsKeys` (`src/queries/analytics.ts`), not here.
 * Invalidated by: station create (`CreateStation`), and from PR 2 every station save, for
 * `recentStation()`. The news, offers, price and market cards never change from a save.
 */
export const dashboardKeys = {
  all: ['dashboard'] as const,
  recentStation: () => [...dashboardKeys.all, 'recentStation'] as const,
  news: () => [...dashboardKeys.all, 'news'] as const,
  offers: () => [...dashboardKeys.all, 'offers'] as const,
  recommendedPrice: (params: { timezone: string }) =>
    [...dashboardKeys.all, 'recommendedPrice', stableParams(params)] as const,
  averagePrice: (params: TGetAveragePricePayload) =>
    [...dashboardKeys.all, 'averagePrice', stableParams(params)] as const,
  marketWatch: (period: TMarketWatchPeriod) =>
    [...dashboardKeys.all, 'marketWatch', period] as const,
};

/**
 * Query for the partner's most recently used station (`{ recentStation }`).
 * What: resolves to `response.data.data`.
 * Key: `['dashboard', 'recentStation']`.
 * Invalidated by: W3 `createStation` (today `CreateStation.tsx`), W1 `updateStation` (PR 2).
 */
export const recentStationQuery = () => ({
  queryKey: dashboardKeys.recentStation(),
  queryFn: ({ signal }: { signal?: AbortSignal }) =>
    getRecentStation(signal).then((response) => unwrapData(response.data.data)),
});

/**
 * Query for the news feed card (currently hidden).
 * Key: `['dashboard', 'news']`. Invalidated by: no save.
 */
export const newsQuery = () => ({
  queryKey: dashboardKeys.news(),
  queryFn: ({ signal }: { signal?: AbortSignal }) =>
    getNews(signal).then((response) => unwrapData(response.data.data)),
});

/**
 * Query for the offers card (currently hidden).
 * Key: `['dashboard', 'offers']`. Invalidated by: no save.
 */
export const offersQuery = () => ({
  queryKey: dashboardKeys.offers(),
  queryFn: ({ signal }: { signal?: AbortSignal }) =>
    getOffers(signal).then((response) => unwrapData(response.data.data)),
});

/**
 * Query for the recommended price card (currently hidden). Resolves to the price breakdown or
 * `null` when none is available.
 * Key: `['dashboard', 'recommendedPrice', { timezone }]`. Invalidated by: no save.
 */
export const recommendedPriceQuery = (params: { timezone: string }) => {
  const stable = stableParams(params);

  return {
    queryKey: dashboardKeys.recommendedPrice(stable),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      getRecommendedPrice(stable, signal).then((response) => unwrapData(response.data.data)),
  };
};

/**
 * Query for one average-price card (currently hidden): national or local, one fuel grade.
 * Key: `['dashboard', 'averagePrice', { type, fuelType, timezone }]`. Invalidated by: no save.
 */
export const averagePriceQuery = (params: TGetAveragePricePayload) => {
  const stable = stableParams(params);

  return {
    queryKey: dashboardKeys.averagePrice(stable),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      getAveragePrice(stable, signal).then((response) => unwrapData(response.data.data)),
  };
};

/**
 * Query for the market watch card (currently hidden): crude oil points for one period.
 * Key: `['dashboard', 'marketWatch', period]`. Invalidated by: no save.
 */
export const marketWatchQuery = (period: TMarketWatchPeriod) => ({
  queryKey: dashboardKeys.marketWatch(period),
  queryFn: ({ signal }: { signal?: AbortSignal }) =>
    getMarketWatch(period, signal).then((response) => unwrapData(response.data.data)),
});
